import en from '@/i18n/en.json';
import es from '@/i18n/es.json';
import pt from '@/i18n/pt.json';
import type { Locale } from './locales';

/** English is the reference shape — every other file must match it exactly. */
type MessageTree = typeof en;

/**
 * Structural mirror of the reference tree. Applying it to es/pt turns a
 * missing translation into a compile error rather than a runtime fallback,
 * which is the rule stated in src/modules/CLAUDE.md.
 */
type SameShape<T> = {
  [K in keyof T]: T[K] extends string ? string : SameShape<T[K]>;
};

const _es = es satisfies SameShape<MessageTree>;
const _pt = pt satisfies SameShape<MessageTree>;

/** Dotted paths to every leaf: 'settings.theme.light' | 'nav.home' | … */
type LeafPaths<T> = T extends string
  ? never
  : {
      [K in keyof T & string]: T[K] extends string
        ? K
        : `${K}.${LeafPaths<T[K]>}`;
    }[keyof T & string];

export type MessageKey = LeafPaths<MessageTree>;

type Nested = { readonly [key: string]: string | Nested };

function flatten(tree: Nested, prefix = '', out: Record<string, string> = {}) {
  for (const [key, value] of Object.entries(tree)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') out[path] = value;
    else flatten(value, path, out);
  }
  return out;
}

export const MESSAGES: Readonly<Record<Locale, Readonly<Record<string, string>>>> =
  {
    en: flatten(en),
    es: flatten(_es),
    pt: flatten(_pt),
  };
